import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { clearSites, selectSite } from 'features/topbar/selectedSiteSlice';
import {
  changeBudgetScenarioName,
  deleteBudgetScenario,
  updateBudgetId
} from '../edit-budget-scenario.slice';
import { logout } from 'features/auth/authenticationSlice';

export interface MmmControlState {
  selectedSegmentKey?: string;
  selectedTimeType?: string;
  selectedBudgetScenarios: {
    primarySelectedBudgetScenario?: { budgetId: string; scenarioName: string };
    comparedSelectedBudgetScenario?: { budgetId: string; scenarioName: string };
  };
  isNewBudgetScenarioMessageOpen: boolean;
  selectedModelRunDetails: {
    targetDate?: string;
    modelRunName?: string;
  };
  budgetScenarioIDInNameEditMode?: string;
}

const initialState: MmmControlState = {
  selectedSegmentKey: undefined,
  selectedTimeType: undefined,
  selectedBudgetScenarios: {
    primarySelectedBudgetScenario: undefined,
    comparedSelectedBudgetScenario: undefined
  },
  isNewBudgetScenarioMessageOpen: false,
  selectedModelRunDetails: {
    targetDate: undefined,
    modelRunName: undefined
  },
  budgetScenarioIDInNameEditMode: undefined
};

const mmmControlSlice = createSlice({
  name: 'mmmControl',
  initialState,
  reducers: {
    setSelectedSegmentKey: (state, { payload }: { payload: string }) => {
      state.selectedSegmentKey = payload;
    },
    setSelectedTimeType: (state, { payload }: { payload: string }) => {
      state.selectedTimeType = payload;
    },
    overrideComparedSelectedBudgetScenario: (
      state,
      {
        payload
      }: PayloadAction<{
        budgetId: string;
        scenarioName: string;
      }>
    ) => {
      state.selectedBudgetScenarios.comparedSelectedBudgetScenario = {
        budgetId: payload.budgetId,
        scenarioName: payload.scenarioName
      };
    },
    setSelectedBudgetScenario: (
      state,
      { payload }: PayloadAction<{ budgetId: string; scenarioName: string }>
    ) => {
      const isPrimarySelectedBudgetScenario =
        payload.budgetId ===
        state.selectedBudgetScenarios.primarySelectedBudgetScenario?.budgetId;

      const isComparedSelectedBudgetScenario =
        payload.budgetId ===
        state.selectedBudgetScenarios.comparedSelectedBudgetScenario?.budgetId;

      if (isPrimarySelectedBudgetScenario) {
        state.selectedBudgetScenarios.primarySelectedBudgetScenario = undefined;
      } else if (
        !state.selectedBudgetScenarios.primarySelectedBudgetScenario &&
        !isComparedSelectedBudgetScenario &&
        !state.selectedBudgetScenarios.comparedSelectedBudgetScenario
      ) {
        state.selectedBudgetScenarios.primarySelectedBudgetScenario = {
          budgetId: payload.budgetId,
          scenarioName: payload.scenarioName
        };
      } else if (isComparedSelectedBudgetScenario) {
        state.selectedBudgetScenarios.comparedSelectedBudgetScenario =
          undefined;
      } else if (
        !state.selectedBudgetScenarios.comparedSelectedBudgetScenario &&
        state.selectedBudgetScenarios.primarySelectedBudgetScenario &&
        !isPrimarySelectedBudgetScenario
      ) {
        state.selectedBudgetScenarios.comparedSelectedBudgetScenario = {
          budgetId: payload.budgetId,
          scenarioName: payload.scenarioName
        };
      }
    },
    setBudgetScenarioNameEditMode: (
      state,
      { payload }: PayloadAction<{ budgetId: string }>
    ) => {
      if (state.budgetScenarioIDInNameEditMode === payload.budgetId) {
        state.budgetScenarioIDInNameEditMode = undefined;
      } else {
        state.budgetScenarioIDInNameEditMode = payload.budgetId;
      }
    },
    setSelectedModelRunDetails: (
      state,
      { payload }: { payload: { targetDate: string; modelRunName: string } }
    ) => {
      state.selectedModelRunDetails = payload;
    },
    openNewBudgetScenarioMessage: (state) => {
      state.isNewBudgetScenarioMessageOpen = true;
    },
    closeNewBudgetScenarioMessage: (state) => {
      state.isNewBudgetScenarioMessageOpen = false;
    }
  },
  extraReducers: (builder) => {
    builder.addCase('PURGE', () => {
      return initialState;
    });
    builder.addCase(logout, () => {
      return initialState;
    });
    builder.addCase(selectSite, () => {
      return initialState;
    });
    builder.addCase(clearSites, () => {
      return initialState;
    });
    builder.addCase(
      deleteBudgetScenario,
      (state, action: PayloadAction<{ budgetId: string }>) => {
        const { budgetId } = action.payload;

        const isPrimarySelectedBudgetScenario =
          budgetId ===
          state.selectedBudgetScenarios.primarySelectedBudgetScenario?.budgetId;

        const isComparedSelectedBudgetScenario =
          budgetId ===
          state.selectedBudgetScenarios.comparedSelectedBudgetScenario
            ?.budgetId;

        if (isPrimarySelectedBudgetScenario) {
          state.selectedBudgetScenarios.primarySelectedBudgetScenario =
            undefined;
        } else if (isComparedSelectedBudgetScenario) {
          state.selectedBudgetScenarios.comparedSelectedBudgetScenario =
            undefined;
        }

        if (state.budgetScenarioIDInNameEditMode === budgetId) {
          state.budgetScenarioIDInNameEditMode = undefined;
        }
      }
    );
    builder.addCase(changeBudgetScenarioName, (state, action) => {
      const { budgetId, scenarioName } = action.payload;

      const isPrimarySelectedBudgetScenario =
        budgetId ===
        state.selectedBudgetScenarios.primarySelectedBudgetScenario?.budgetId;

      const isComparedSelectedBudgetScenario =
        budgetId ===
        state.selectedBudgetScenarios.comparedSelectedBudgetScenario?.budgetId;

      if (state.budgetScenarioIDInNameEditMode === budgetId) {
        state.budgetScenarioIDInNameEditMode = undefined;
      }

      const newBudgetId = updateBudgetId(budgetId, scenarioName);

      if (isPrimarySelectedBudgetScenario) {
        state.selectedBudgetScenarios.primarySelectedBudgetScenario = {
          budgetId: newBudgetId,
          scenarioName
        };
      } else if (isComparedSelectedBudgetScenario) {
        state.selectedBudgetScenarios.comparedSelectedBudgetScenario = {
          budgetId: newBudgetId,
          scenarioName
        };
      }

      state.isNewBudgetScenarioMessageOpen = false;
    });
  }
});

export const {
  setSelectedSegmentKey,
  setSelectedTimeType,
  setSelectedBudgetScenario,
  setSelectedModelRunDetails,
  openNewBudgetScenarioMessage,
  closeNewBudgetScenarioMessage,
  setBudgetScenarioNameEditMode,
  overrideComparedSelectedBudgetScenario
} = mmmControlSlice.actions;

export const mmmControlReducer = mmmControlSlice.reducer;

export const mmmControlSliceName = mmmControlSlice.name;

export const selectSelectedModelRunDetails = createSelector(
  (state: RootState) => state.mmmControl,
  (mmmControl) => mmmControl.selectedModelRunDetails
);

export const selectSelectedSegmentKey = createSelector(
  (state: RootState) => state.mmmControl,
  (mmmControl) => mmmControl.selectedSegmentKey
);

export const selectSelectedTimeType = createSelector(
  (state: RootState) => state.mmmControl,
  (mmmControl) => mmmControl.selectedTimeType
);

export const selectSelectedBudgetScenarios = createSelector(
  (state: RootState) => state.mmmControl,
  (mmmControl) => mmmControl.selectedBudgetScenarios
);

export const selectIsNewBudgetScenarioMessageOpen = createSelector(
  (state: RootState) => state.mmmControl,
  (mmmControl) => mmmControl.isNewBudgetScenarioMessageOpen
);

export const selectBudgetScenarioIDInNameEditMode = createSelector(
  (state: RootState) => state.mmmControl,
  (mmmControl) => mmmControl.budgetScenarioIDInNameEditMode
);

export const selectBudgetScenarioLabel = createSelector(
  (state: RootState) => state.mmmControl,
  (mmmControl) => {
    const selectedBudgetScenarios = [
      mmmControl.selectedBudgetScenarios.primarySelectedBudgetScenario
        ?.scenarioName,
      mmmControl.selectedBudgetScenarios.comparedSelectedBudgetScenario
        ?.scenarioName
    ].filter((budgetScenario) => budgetScenario !== undefined);
    return selectedBudgetScenarios.length > 0
      ? selectedBudgetScenarios.slice(0, 2).join(' / ')
      : 'Select Budget Scenario';
  }
);
